<template>
  <div class="new-patient-report">
    <!-- Header with Close Button -->
    <b-navbar class="px-2 mb-3" toggleable="md" type="dark" variant="primary">
      <b-navbar-brand href="#">
        <span class="ml-4">New Patient Report</span>
      </b-navbar-brand>
      <b-navbar-toggle class="ml-auto" target="nav_collapse_npr"></b-navbar-toggle>
      <b-collapse is-nav id="nav_collapse_npr">
        <b-navbar-nav class="ml-auto">
          <b-nav-item href="#">
            <button type="button" id="close-tooltip-npr" class="btn mr-xs btn-default btn-sm" @click="closeSlideOutPanel">
              <i class="la la-remove"></i>
            </button>
            <b-tooltip target="close-tooltip-npr" triggers="hover">
              Close
            </b-tooltip>
          </b-nav-item>
        </b-navbar-nav>
      </b-collapse>
    </b-navbar>

    <PopupErrorDisplay />

    <b-overlay :show="reportLoading" rounded="sm">
      <b-container fluid>
        <!-- Collapsed summary bar -->
        <div v-if="!showFilters || !showChart" class="collapsed-summary-bar mb-3">
          <div class="d-flex align-items-center flex-wrap gap-2">
            <div v-if="!showFilters" class="summary-info">
              <i class="fa fa-calendar-o mr-1"></i>
              <span>{{ formatDateRange(filters.fromDate, filters.toDate) }}</span>
              <span v-if="filters.provider" class="ml-2">| {{ filters.provider.Provider }}</span>
              <span v-if="filteredPatientData.length > 0" class="ml-2 badge badge-primary">{{ filteredPatientData.length }} patients</span>
            </div>
            <div class="ml-auto d-flex gap-2">
              <button v-if="!showFilters" class="btn btn-sm btn-outline-secondary" @click="showFilters = true">
                <i class="fa fa-filter mr-1"></i> Show Filters
              </button>
              <button v-if="!showChart" class="btn btn-sm btn-outline-secondary" @click="showChart = true">
                <i class="fa fa-bar-chart mr-1"></i> Show Chart
              </button>
            </div>
          </div>
        </div>

        <!-- Top Section: Filters (Left) and Referral Chart (Right) -->
        <b-row class="top-section-row" v-if="showFilters || showChart">
          <!-- Left Panel: Filters -->
          <b-col :lg="showFilters && showChart ? '6' : '12'" md="12" class="mb-3" v-if="showFilters">
            <Widget customHeader>
              <template #title>
                <div class="d-flex align-items-center justify-content-between w-100">
                  <h5 class="fw-bold mb-0">Search & Filter</h5>
                  <button class="btn btn-sm btn-link text-muted p-0 ml-2" @click="showFilters = false" title="Collapse filters">
                    <i class="fa fa-chevron-up"></i>
                  </button>
                </div>
              </template>
              
              <b-row>
                <!-- Date Range Pickers -->
                <b-col cols="12" sm="6" md="3" class="mb-3">
                  <label class="form-label fw-semi-bold">From</label>
                  <DatePicker
                    v-model="filters.fromDate"
                    lang="en"
                    :format="dateFormatStr"
                    input-class="form-control"
                    @change="onDateChange"
                  >
                    <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                  </DatePicker>
                </b-col>
                <b-col cols="12" sm="6" md="3" class="mb-3">
                  <label class="form-label fw-semi-bold">To</label>
                  <DatePicker
                    v-model="filters.toDate"
                    lang="en"
                    :format="dateFormatStr"
                    input-class="form-control"
                    @change="onDateChange"
                  >
                    <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                  </DatePicker>
                </b-col>
                <b-col cols="12" sm="12" md="6" class="mb-3">
                  <label class="form-label fw-semi-bold d-none d-md-block" style="visibility: hidden;">Quick Filters</label>
                  <b-button-group class="w-100 quick-date-buttons">
                    <b-button
                      @click="setThisMonth"
                      :variant="isThisMonth ? 'primary' : 'outline-primary'"
                      size="sm"
                      class="flex-fill"
                    >
                      This Month
                    </b-button>
                    <b-button
                      @click="setLastMonth"
                      :variant="isLastMonth ? 'primary' : 'outline-primary'"
                      size="sm"
                      class="flex-fill"
                    >
                      Last Month
                    </b-button>
                  </b-button-group>
                </b-col>
              </b-row>

              <b-row>
                <!-- Provider Dropdown -->
                <b-col cols="12" sm="6" md="6" class="mb-3">
                  <label class="form-label fw-semi-bold">Provider</label>
                  <multiselect
                    v-model="filters.provider"
                    :options="providerOptions"
                    :multiple="false"
                    :taggable="false"
                    :show-labels="false"
                    label="Provider"
                    track-by="ProvNum"
                    placeholder="All Providers"
                    :allow-empty="true"
                  />
                </b-col>

                <!-- Initial Visit Codes -->
                <b-col cols="12" sm="6" md="6" class="mb-3">
                  <label class="form-label fw-semi-bold">Initial Visit Codes</label>
                  <multiselect
                    v-if="procCodes"
                    v-model="filters.initialVisitCodes"
                    :options="procCodes"
                    :multiple="true"
                    :taggable="false"
                    :show-labels="false"
                    :custom-label="codeWithDesc"
                    label="ProcCode"
                    track-by="ProcCode"
                    placeholder="Select codes..."
                    @search-change="searchCodes"
                  >
                    <template slot="tag" slot-scope="props">
                      <span class="custom__tag">
                        <span>{{props.option.ProcCode}}</span>
                        <i
                          tabindex="1"
                          class="multiselect__tag-icon"
                          @click="removeCode(props.option, 'initialVisitCodes')"
                        ></i>
                      </span>
                    </template>
                    <template slot="option" slot-scope="props">
                      <div class="option__desc">
                        <div class="option__title d-flex">
                          <span class="option-code">{{ codeWithDesc(props.option) }}</span>
                        </div>
                      </div>
                    </template>
                  </multiselect>
                </b-col>
              </b-row>

              <b-row>
                <!-- Referral Source -->
                <b-col cols="12" sm="6" md="6" class="mb-3">
                  <label class="form-label fw-semi-bold">Referral Source</label>
                  <multiselect
                    v-model="filters.referralSource"
                    :options="referralSourceOptions"
                    :multiple="false"
                    :taggable="false"
                    :show-labels="false"
                    placeholder="All Sources"
                    :allow-empty="true"
                  />
                </b-col>

                <!-- Initial Appointment Status -->
                <b-col cols="12" sm="6" md="6" class="mb-3">
                  <label class="form-label fw-semi-bold">Initial Appointment Status</label>
                  <multiselect
                    v-model="selectedInitialAptStatus"
                    :options="initialAptStatusOptions"
                    :multiple="false"
                    :searchable="false"
                    :show-labels="false"
                    :allow-empty="false"
                    label="label"
                    track-by="value"
                    placeholder="Show All"
                    @select="onInitialAptStatusChange"
                  />
                </b-col>
              </b-row>

              <b-row>
                <!-- Next Appointment Status -->
                <b-col cols="12" sm="6" md="6" class="mb-3">
                  <label class="form-label fw-semi-bold">Next Appointment Status</label>
                  <multiselect
                    v-model="selectedNextAptStatus"
                    :options="nextAptStatusOptions"
                    :multiple="false"
                    :searchable="false"
                    :show-labels="false"
                    :allow-empty="false"
                    label="label"
                    track-by="value"
                    placeholder="Show All"
                    @select="onNextAptStatusChange"
                  />
                </b-col>

                <!-- Next Appointment Procedure Codes -->
                <b-col cols="12" sm="6" md="6" class="mb-3">
                  <label class="form-label fw-semi-bold">Next Appointment Procedure Codes</label>
                  <multiselect
                    v-if="procCodes"
                    v-model="filters.nextAptProcCodes"
                    :options="procCodes"
                    :multiple="true"
                    :taggable="false"
                    :show-labels="false"
                    :custom-label="codeWithDesc"
                    label="ProcCode"
                    track-by="ProcCode"
                    placeholder="Select codes..."
                    @search-change="searchCodes"
                  >
                    <template slot="tag" slot-scope="props">
                      <span class="custom__tag">
                        <span>{{props.option.ProcCode}}</span>
                        <i
                          tabindex="1"
                          class="multiselect__tag-icon"
                          @click="removeCode(props.option, 'nextAptProcCodes')"
                        ></i>
                      </span>
                    </template>
                    <template slot="option" slot-scope="props">
                      <div class="option__desc">
                        <div class="option__title d-flex">
                          <span class="option-code">{{ codeWithDesc(props.option) }}</span>
                        </div>
                      </div>
                    </template>
                  </multiselect>
                </b-col>
              </b-row>

              <b-row class="mt-3">
                <b-col cols="12" class="d-flex flex-wrap gap-2 action-buttons">
                  <b-button variant="primary" @click="search" :disabled="reportLoading" class="flex-sm-grow-0 flex-grow-1">
                    <i class="fa fa-search mr-1"></i> Search
                  </b-button>
                  <b-button variant="outline-secondary" @click="resetFilters" class="flex-sm-grow-0 flex-grow-1">
                    <i class="fa fa-refresh mr-1"></i> Reset
                  </b-button>
                </b-col>
              </b-row>
            </Widget>
          </b-col>


          <!-- Right Panel: Referral Chart -->
          <b-col :lg="showFilters && showChart ? '6' : '12'" md="12" class="mb-3" v-if="showChart">
            <Widget customHeader class="chart-widget-full">
              <template #title>
                <div class="d-flex align-items-center justify-content-between w-100">
                  <h5 class="fw-bold mb-0">Referral Source Analysis</h5>
                  <button class="btn btn-sm btn-link text-muted p-0 ml-2" @click="showChart = false" title="Collapse chart">
                    <i class="fa fa-chevron-up"></i>
                  </button>
                </div>
              </template>

              <!-- Horizontal Bar Chart -->
              <div class="chart-section-wrapper">
                <div class="date-range-label">
                  <i class="fa fa-calendar-o"></i>
                  <span>{{ formatDateRange(filters.fromDate, filters.toDate) }}</span>
                </div>

                <div v-if="sortedReferralSources && sortedReferralSources.length > 0" class="referral-bar-chart">
                  <div
                    v-for="(item, index) in sortedReferralSources"
                    :key="index"
                    class="referral-bar-row"
                  >
                    <div class="referral-bar-label" :title="item.referralSource">{{ item.referralSource }}</div>
                    <div class="referral-bar-track">
                      <div
                        class="referral-bar-fill"
                        :style="{
                          width: (maxReferralCount > 0 ? (item.count / maxReferralCount * 100) : 0) + '%',
                          backgroundColor: chartColors[index % chartColors.length]
                        }"
                      ></div>
                    </div>
                    <div class="referral-bar-count">{{ item.count }}</div>
                  </div>
                </div>
                <div v-else class="empty-state-box chart-empty-state">
                  <div class="empty-icon">
                    <i class="fa fa-bar-chart"></i>
                  </div>
                  <h5 class="empty-title">No Data Available</h5>
                  <p class="empty-text">Referral source data will appear here once patients are recorded.</p>
                </div>
              </div>
            </Widget>
          </b-col>
        </b-row>

        <!-- Bottom Section: Patient Data Table - Full Width -->
        <b-row>
          <b-col cols="12">
            <!-- Data Table -->
            <Widget customHeader v-if="filteredPatientData && filteredPatientData.length > 0">
              <template #title>
                <h5 class="fw-bold mb-0">
                  Patient Data 
                  <span class="badge badge-primary ml-2">{{ filteredPatientData.length }}</span>
                </h5>
              </template>
              <!-- Top Pagination -->
              <div v-if="totalPages > 1" class="top-pagination d-flex align-items-center justify-content-between px-3 py-2">
                <span class="pagination-info">
                  Showing {{ paginationStart }}–{{ paginationEnd }} of {{ filteredPatientData.length }} patients
                </span>
                <div class="d-flex align-items-center gap-2">
                  <button class="btn btn-sm btn-outline-secondary" :disabled="currentPage <= 1" @click="goToPage(currentPage - 1)">
                    <i class="fa fa-chevron-left"></i>
                  </button>
                  <span class="pagination-page">Page {{ currentPage }} of {{ totalPages }}</span>
                  <button class="btn btn-sm btn-outline-secondary" :disabled="currentPage >= totalPages" @click="goToPage(currentPage + 1)">
                    <i class="fa fa-chevron-right"></i>
                  </button>
                </div>
              </div>
              <div class="table-responsive">
                <v-client-table
                  v-if="filteredPatientData && filteredPatientData.length > 0"
                  :data="filteredPatientData"
                  :columns="tableColumns"
                  :options="tableOptions"
                  ref="patientTable"
                  @pagination="onPagination"
                >
                  <!-- Header slots -->
                  <div slot="h__Patient">Patient</div>
                  <div slot="h__Created">Created</div>
                  <div slot="h__FirstVisit">First Visit</div>
                  <div slot="h__Provider">Provider</div>
                  <div slot="h__Appointment">Appointment</div>
                  <div slot="h__NextScheduled">Next Scheduled</div>
                  <div slot="h__NextCompleted">Next Completed</div>
                  <div slot="h__ReferredBy">Referred By</div>

                  <!-- Patient Column: Display value from mapped "Patient" property -->
                  <div slot="Patient" slot-scope="props">
                    <a href="#" @click.prevent="gotoPatientPage(props.row.PatNum)">
                      {{ props.row.Patient || props.row.patient || '' }}
                    </a>
                  </div>

                  <!-- Created Column: Display value from mapped "Created" property (SecDateEntry formatted) -->
                  <div slot="Created" slot-scope="props">
                    <small v-if="props.row.Created">
                      {{ formatDateOnly(props.row.Created) }}
                    </small>
                  </div>

                  <!-- First Visit Column: Display value from mapped "FirstVisit" property (date only) -->
                  <div slot="FirstVisit" slot-scope="props">
                    <small v-if="props.row.FirstVisit || props.row.firstVisit">
                      {{ formatDateOnly(props.row.FirstVisit || props.row.firstVisit) }}
                    </small>
                  </div>

                  <!-- Provider Column: Display value from mapped "Provider" property -->
                  <div slot="Provider" slot-scope="props">
                    {{ props.row.Provider || props.row.provider || '' }}
                  </div>

                  <!-- Appointment Column: Display value from mapped "Appointment" property + AptStatus badge -->
                  <!-- Rule: display AppointmentDescription, and right below it, display in a "tag" format the status -->
                  <!-- Status: "Scheduled" when AptStatus = 1, "Completed" when AptStatus = 2 -->
                  <div slot="Appointment" slot-scope="props">
                    <div>{{ props.row.Appointment || props.row.AppointmentDescription || '' }}</div>
                    <b-badge 
                      v-if="props.row.AptStatus === 1 || props.row.AptStatus === 2"
                      :variant="getAptStatusVariant(props.row.AptStatus)" 
                      class="mt-1">
                      {{ getAptStatusText(props.row.AptStatus) }}
                    </b-badge>
                  </div>

                  <!-- Next Scheduled Column: Display value from mapped "NextScheduled" property + status tag + date -->
                  <div slot="NextScheduled" slot-scope="props">
                    <div v-if="props.row.NextScheduled || props.row.nextScheduledProcedures">
                      <div>{{ props.row.NextScheduled || props.row.nextScheduledProcedures }}</div>
                      <!-- Status badge below the procedures description -->
                      <b-badge v-if="props.row.nextScheduledAptStatus || props.row.nextScheduledAptStatus === 0" 
                        :variant="getAptStatusVariant(props.row.nextScheduledAptStatus)" 
                        class="mt-1">
                        {{ getAptStatusText(props.row.nextScheduledAptStatus) }}
                      </b-badge>
                      <!-- Date below the status badge -->
                      <div class="small text-muted mt-1 fw-bold" v-if="props.row.nextScheduledVisit || props.row.nextScheduledVisit">
                        {{ formatDateOnly(props.row.nextScheduledVisit) }}
                      </div>
                    </div>
                    <!-- If no next scheduled appointment, show "Not scheduled" in red badge -->
                    <b-badge v-else variant="danger">Not scheduled</b-badge>
                  </div>

                  <!-- Next Completed Column: Display value from mapped "NextCompleted" property + date -->
                  <div slot="NextCompleted" slot-scope="props">
                    <div v-if="props.row.NextCompleted || props.row.completedAfterInitialVisitProcDescript">
                      <div>{{ props.row.NextCompleted || props.row.completedAfterInitialVisitProcDescript }}</div>
                      <!-- Date below the procedures description -->
                      <div class="small text-muted mt-1 fw-bold" v-if="props.row.completedAfterInitialVisitAptDate">
                        {{ formatDateOnly(props.row.completedAfterInitialVisitAptDate) }}
                      </div>
                    </div>
                    <!-- If null, leave blank (don't show anything) -->
                  </div>

                  <!-- Referred By Column: Display value from mapped "ReferredBy" property -->
                  <div slot="ReferredBy" slot-scope="props">
                    <span v-if="props.row.ReferredBy">{{ props.row.ReferredBy }}</span>
                  </div>
                </v-client-table>
              </div>
            </Widget>

            <div v-else class="empty-state-box mt-3">
              <div class="empty-icon">
                <i class="fa fa-users"></i>
              </div>
              <h5 class="empty-title">No Data</h5>
              <p class="empty-text">No new patients found for the selected filters. Try adjusting your search criteria.</p>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </b-overlay>
  </div>
</template>

<script>
/* eslint-disable */

import Vue from 'vue';
import Widget from '@/components/Widget/Widget';
import mixin from '../../../mixin.js';
import PopupErrorDisplay from "@/components/ErrorDisplay/PopupErrorDisplay";
import { mapState, mapActions } from 'vuex';
import moment from 'moment-timezone';
import _ from 'lodash';
import DatePicker from "vue2-datepicker";
import Multiselect from "vue-multiselect";
export default {
  name: 'NewPatientReport',
  components: { Widget, PopupErrorDisplay, DatePicker, Multiselect },
  props: {
    initialTimeframe: {
      type: String,
      default: 'month-to-date' // 'today' or 'month-to-date'
    },
    huddleDate: {
      type: String,
      default: null // 'YYYY-MM-DD' — anchors default date ranges to the Huddle's selected date
    }
  },
  mixins: [mixin],

  data() {
    return {
      reportLoading: false,
      comparisonLoading: false,
      showFilters: true,
      showChart: true,
      patientData: [],
      referralSourcesData: [],
      providersFromData: [],
      
      filters: {
        fromDate: null,
        toDate: null,
        provider: null,
        initialVisitCodes: [],
        referralSource: null,
        initialAptStatus: 'all',
        nextAptStatus: 'all',
        nextAptProcCodes: []
      },
      
      comparison: {
        fromDate: null,
        toDate: null,
        usePreviousYear: false
      },
      
      currentPage: 1,
      showComparisonChart: false,
      comparisonReferralData: [],
      
      // Note: Column names here must match slot names
      // v-client-table will use slots if provided, otherwise it displays the data property directly
      tableColumns: ['Patient', 'Created', 'FirstVisit', 'Provider', 'Appointment', 'NextScheduled', 'NextCompleted', 'ReferredBy'],
      tableOptions: {
        filterable: ['Patient', 'Provider'],
        skin: 'table table-striped table-hover',
        sortable: ['Patient', 'FirstVisit', 'Provider'],
        perPage: 25,
        sortByColumn: false,
        pagination: { chunk: 10, dropdown: false },
        headings: {
          Patient: 'Patient',
          Created: 'Created',
          FirstVisit: 'First Visit',
          Provider: 'Provider',
          Appointment: 'Appointment',
          NextScheduled: 'Next Scheduled',
          NextCompleted: 'Next Completed',
          ReferredBy: 'Referred By'
        }
      },
      
      // Chart colors
      // Softer, muted color palette - distinct colors to avoid similar shades
      chartColors: [
        '#3abf94',  // Brand green (primary)
        '#93c5fd',  // Light blue
        '#f9a8d4',  // Light pink
        '#fcd34d',  // Light amber
        '#a5b4fc',  // Light indigo
        '#64748b',  // Slate gray
        '#f97316',  // Orange
        '#c4b5fd',  // Light violet
        '#06b6d4',  // Cyan
        '#cbd5e1',  // Light slate
      ],

      // Multiselect options for appointment status dropdowns
      initialAptStatusOptions: [
        { value: 'all', label: 'Show All' },
        { value: '1', label: 'Scheduled' },
        { value: '2', label: 'Completed' }
      ],
      selectedInitialAptStatus: { value: 'all', label: 'Show All' },

      nextAptStatusOptions: [
        { value: 'all', label: 'Show All' },
        { value: '1', label: 'Scheduled' },
        { value: '2', label: 'Completed' },
        { value: 'notScheduled', label: 'Not Scheduled' }
      ],
      selectedNextAptStatus: { value: 'all', label: 'Show All' }
    };
  },

  computed: {
    ...mapState({
      procCodes: state => state.customer.procCodes
    }),

    dateFormatStr() {
      return 'MM/DD/YYYY';
    },

    providerOptions() {
      return this.providersFromData || [];
    },

    referralSourceOptions() {
      // Rule: Dropdown should have:
      // 1. "Patients" (always included - the word, not actual patient names)
      // 2. "Not Available" (always included)
      // 3. Unique values from referredByLName where referredByFName is NULL AND (NotPerson = 1 OR NotPerson = 0)
      //    (business/organization referrals with no first name)
      const options = ['Patients', 'Not Available'];
      
      if (!this.patientData || this.patientData.length === 0) {
        return options;
      }
      
      // Extract unique business/organization referral sources
      // Include: referredByLName where referredByFName is NULL AND (NotPerson = 1 OR NotPerson = 0)
      // This captures business/organization names regardless of NotPerson value, as long as there's no first name
      const uniqueSources = new Set();
      this.patientData.forEach(patient => {
        if (patient.referredByLName && patient.referredByLName.trim() !== '' && 
            !patient.referredByFName && 
            (patient.NotPerson === 1 || patient.NotPerson === 0)) {
          uniqueSources.add(patient.referredByLName);
        }
      });
      
      // Add unique business/organization sources to options (sorted alphabetically)
      const businessSources = Array.from(uniqueSources).sort();
      options.push(...businessSources);
      
      return options;
    },

    filteredPatientData() {
      if (!this.patientData || this.patientData.length === 0) {
        return [];
      }

      let filtered = [...this.patientData];

      // Apply referral source filter (frontend filter since backend doesn't handle this directly)
      if (this.filters.referralSource) {
        filtered = filtered.filter(patient => {
          if (this.filters.referralSource === 'Patients') {
            // Show rows where referredByLName is NOT NULL AND referredByFName is NOT NULL AND NotPerson = 0
            return patient.referredByLName && patient.referredByFName && patient.NotPerson === 0;
          } else if (this.filters.referralSource === 'Not Available') {
            // Show rows where referredByLName is NULL AND referredByFName is NULL AND NotPerson is NULL
            return !patient.referredByLName && !patient.referredByFName && (patient.NotPerson === null || patient.NotPerson === undefined);
          } else {
            // Business/Organization source - match by referredByLName
            // Include if referredByFName is NULL AND (NotPerson = 1 OR NotPerson = 0)
            return patient.referredByLName === this.filters.referralSource && 
                   !patient.referredByFName &&
                   (patient.NotPerson === 1 || patient.NotPerson === 0);
          }
        });
      }

      // Apply next appointment status filter (frontend filter for "Not Scheduled" option)
      if (this.filters.nextAptStatus && this.filters.nextAptStatus !== 'all') {
        filtered = filtered.filter(patient => {
          if (this.filters.nextAptStatus === 'notScheduled') {
            // Show rows where nextScheduledAptStatus != 1 AND nextScheduledAptStatus != 2
            return patient.nextScheduledAptStatus !== 1 && patient.nextScheduledAptStatus !== 2;
          } else {
            // For "Scheduled" (1) or "Completed" (2), match the status value
            return patient.nextScheduledAptStatus === parseInt(this.filters.nextAptStatus, 10);
          }
        });
      }

      return filtered;
    },

    sortedReferralSources() {
      if (!this.referralSourcesData || this.referralSourcesData.length === 0) {
        return [];
      }
      return [...this.referralSourcesData]
        .filter(item => item.referralSource)
        .sort((a, b) => b.count - a.count);
    },

    maxReferralCount() {
      if (!this.sortedReferralSources || this.sortedReferralSources.length === 0) return 0;
      return this.sortedReferralSources[0].count;
    },

    totalPages() {
      if (!this.filteredPatientData || this.filteredPatientData.length === 0) return 0;
      return Math.ceil(this.filteredPatientData.length / this.tableOptions.perPage);
    },

    paginationStart() {
      return (this.currentPage - 1) * this.tableOptions.perPage + 1;
    },

    paginationEnd() {
      return Math.min(this.currentPage * this.tableOptions.perPage, this.filteredPatientData.length);
    },

    isThisMonth() {
      if (!this.filters.fromDate || !this.filters.toDate) return false;
      const startOfMonth = moment().startOf('month');
      const endOfMonth = moment().endOf('month');
      return moment(this.filters.fromDate).isSame(startOfMonth, 'day') &&
             moment(this.filters.toDate).isSame(endOfMonth, 'day');
    },

    isLastMonth() {
      if (!this.filters.fromDate || !this.filters.toDate) return false;
      const startOfLastMonth = moment().subtract(1, 'month').startOf('month');
      const endOfLastMonth = moment().subtract(1, 'month').endOf('month');
      return moment(this.filters.fromDate).isSame(startOfLastMonth, 'day') && 
             moment(this.filters.toDate).isSame(endOfLastMonth, 'day');
    },

    canCompare() {
      return (this.comparison.fromDate && this.comparison.toDate) || this.comparison.usePreviousYear;
    }
  },

  async created() {
    await this.init();
  },

  methods: {
    ...mapActions('huddle', ['getNewPatientReportData', 'getNewPatientReportComparisonData', 'clearNewPatientReportData']),
    ...mapActions('customer', ['getProcCodes']),

    async init() {
      this.resetError();

      // Initialize date range based on initialTimeframe prop, anchored on huddleDate when provided
      if (this.initialTimeframe === 'today') {
        const anchor = this.anchorMoment();
        this.filters.fromDate = anchor.toDate();
        this.filters.toDate = anchor.toDate();
      } else {
        // Default to this month (full month) of the anchor date
        this.setThisMonth();
      }

      // Load proc codes if not already loaded
      if (!this.procCodes || this.procCodes.length === 0) {
        try {
          await this.getProcCodes({ siteID: this.siteID });
        } catch (err) {
          console.error('Error loading proc codes:', err);
        }
      }

      // Load initial data
      await this.search();
    },

    async search() {
      this.reportLoading = true;
      this.currentPage = 1;
      this.resetError();

      try {
        // Build payload following AdvancedTxPlanned pattern (filters at top level)
        const payload = {
          siteID: this.siteID,
          fromDate: moment(this.filters.fromDate).format('YYYY-MM-DD'),
          toDate: moment(this.filters.toDate).format('YYYY-MM-DD'),
          provNum: !this.filters.provider || this.filters.provider === '' ? '' : this.filters.provider.ProvNum,
          initialVisitCodes: this.filters.initialVisitCodes && this.filters.initialVisitCodes.length > 0 
            ? this.filters.initialVisitCodes.map(c => c.ProcCode).join(',') 
            : '',
          initialAptStatus: !this.filters.initialAptStatus || this.filters.initialAptStatus === 'all' ? '' : this.filters.initialAptStatus,
          nextAptProcCodes: this.filters.nextAptProcCodes && this.filters.nextAptProcCodes.length > 0 
            ? this.filters.nextAptProcCodes.map(c => c.ProcCode).join(',') 
            : '',
          nextAptStatus: !this.filters.nextAptStatus || this.filters.nextAptStatus === 'all' || this.filters.nextAptStatus === 'notScheduled' ? '' : this.filters.nextAptStatus
        };
        
        // Following AdvancedTxPlanned pattern: response.data is array of result sets
        // response.data[0] = patients
        // referralSources and providers are computed on frontend from patient data
        const response = await this.getNewPatientReportData(payload);
        
        // Handle response structure
        // Current response structure: { patients: [...], referralSources: [...], providers: [...] }
        if (response && response.data) {
          // Check for structure: { patients: [...], referralSources: [...], providers: [...] }
          if (response.data.patients && Array.isArray(response.data.patients)) {
            this.patientData = response.data.patients || [];
            // Use referralSources and providers from response if available
            this.referralSourcesData = response.data.referralSources || [];
            this.providersFromData = response.data.providers || [];
          }
          // Check for new structure: { data: [patients] }
          else if (response.data.data && Array.isArray(response.data.data)) {
            this.patientData = response.data.data[0] || [];
          }
          // Check if response.data is directly an array (compressed response)
          else if (Array.isArray(response.data)) {
            this.patientData = response.data[0] || [];
          }
        }
        
        // If we got patients but no referralSources/providers from response, compute them
        if (this.patientData && this.patientData.length > 0) {
          // Map data columns to match table column names for v-client-table
          // v-client-table may need the column name to exist in the data object for slots to work
          this.patientData = this.patientData.map(patient => {
            // Compute ReferredBy based on rules:
            // If NotPerson === 0 and referredByFName exists, concatenate referredByLName + ' ' + referredByFName
            // Otherwise, just use referredByLName (or empty if null)
            let referredBy = '';
            if (patient.referredByLName) {
              if (patient.NotPerson === 0 && patient.referredByFName) {
                referredBy = patient.referredByLName + ' ' + patient.referredByFName;
              } else {
                referredBy = patient.referredByLName;
              }
            }
            
            // Compute Created: format SecDateEntry, but only if not '0001-01-01' or NULL
            let created = null;
            if (patient.SecDateEntry && 
                patient.SecDateEntry !== '0001-01-01' && 
                patient.SecDateEntry !== '0001-01-01 00:00:00') {
              created = patient.SecDateEntry;
            }
            
            // For v-client-table: if a slot exists for a column, it will use the slot
            // Otherwise it displays the data property directly
            // We need to keep AppointmentDescription in the data for the slot to access it
            // But we can also set Appointment property - the slot will override it
            // Keep AppointmentDescription as-is - slot will handle the display
            let appointmentDisplay = patient.AppointmentDescription || patient.appointmentDescription || '';
            
            return {
              ...patient,
              // Map backend column names to table column names
              Patient: patient.Patient || '',
              Created: created,
              FirstVisit: patient.FirstVisit || null,
              Provider: patient.Provider || '',
              // Set Appointment property with status text included
              Appointment: appointmentDisplay,
              NextScheduled: patient.nextScheduledProcedures || null,
              NextCompleted: patient.completedAfterInitialVisitProcDescript || null,
              ReferredBy: referredBy,
              // Explicitly preserve AptStatus for reference
              AptStatus: patient.AptStatus !== null && patient.AptStatus !== undefined ? patient.AptStatus : null,
              // Keep AppointmentDescription for reference
              AppointmentDescription: patient.AppointmentDescription || patient.appointmentDescription || '',
              // Keep all original fields for reference
            };
          });
          
          // Only compute if we didn't get them from response (old structure)
          if (!this.referralSourcesData || this.referralSourcesData.length === 0) {
            this.referralSourcesData = this.computeReferralSources(this.patientData);
          }
          if (!this.providersFromData || this.providersFromData.length === 0) {
            this.providersFromData = this.computeProviders(this.patientData);
          }
          
          // DEBUG: Log referral source extraction
          if (this.patientData.length > 0) {
            console.log('=== REFERRAL SOURCE EXTRACTION DEBUG ===');
            const businessSources = new Set();
            this.patientData.forEach(patient => {
              console.log(`Patient: ${patient.Patient}, referredByLName: ${patient.referredByLName}, NotPerson: ${patient.NotPerson}, ReferredBy: ${patient.ReferredBy}`);
              if (patient.NotPerson === 1 && patient.referredByLName && patient.referredByLName.trim() !== '') {
                businessSources.add(patient.referredByLName);
              }
            });
            console.log('Extracted business sources:', Array.from(businessSources));
            console.log('Referral source options:', this.referralSourceOptions);
            console.log('=== END REFERRAL SOURCE DEBUG ===');
          }
        } else {
          this.patientData = [];
          this.referralSourcesData = [];
          this.providersFromData = [];
        }
      } catch (err) {
        console.error('Error searching new patient report:', err);
        this.$toast.error('Error loading new patient report data');
      } finally {
        this.reportLoading = false;
        // Auto-collapse filters and chart after search to give table more space
        if (this.patientData && this.patientData.length > 0) {
          this.showFilters = false;
          this.showChart = false;
        }
      }
    },

    /**
     * Compute referral sources from patient data for bar chart
     * Groups patients by referral source and counts them
     */
    computeReferralSources(patients) {
      if (!patients || patients.length === 0) {
        return [];
      }

      const sourceMap = {};

      patients.forEach(patient => {
        let referralSource = 'Not Available';
        
        // Determine referral source based on the logic from the original query
        if (patient.NotPerson === 1) {
          // Business/Organization source
          referralSource = patient.referredByLName || 'Not Available';
        } else if (patient.NotPerson === 0 && patient.referredByLName && patient.referredByFName) {
          // Person referral - group as "Patients"
          referralSource = 'Patients';
        } else {
          // No referral data
          referralSource = 'Not Available';
        }

        if (!sourceMap[referralSource]) {
          sourceMap[referralSource] = {
            referralSource: referralSource,
            NotPerson: patient.NotPerson,
            count: 0
          };
        }
        sourceMap[referralSource].count++;
      });

      // Convert to array and sort by count descending
      return Object.values(sourceMap).sort((a, b) => b.count - a.count);
    },

    /**
     * Compute unique providers from patient data for dropdown
     * Returns array of unique providers sorted by name
     */
    computeProviders(patients) {
      if (!patients || patients.length === 0) {
        return [];
      }

      const providerMap = {};

      patients.forEach(patient => {
        // Use ProvNum as key to avoid duplicates
        if (patient.ProvNum && patient.Provider) {
          if (!providerMap[patient.ProvNum]) {
            providerMap[patient.ProvNum] = {
              ProvNum: patient.ProvNum,
              Provider: patient.Provider,
              Abbr: patient.ProviderAbbr || null
            };
          }
        }
      });

      // Convert to array and sort by Provider name
      return Object.values(providerMap).sort((a, b) => {
        return (a.Provider || '').localeCompare(b.Provider || '');
      });
    },

    /**
     * Format date to mm-dd-yyyy (date only, no time)
     */
    formatDateOnly(dateValue) {
      if (!dateValue) return '';
      const date = moment(dateValue);
      if (!date.isValid()) return '';
      return date.format('MM/DD/YYYY');
    },

    /**
     * Get AppointmentDescription value (handle case variations)
     */
    getAppointmentDescription(row) {
      // Debug: Log what we're getting
      console.log('getAppointmentDescription - row:', row);
      console.log('getAppointmentDescription - AppointmentDescription:', row?.AppointmentDescription);
      console.log('getAppointmentDescription - appointmentDescription:', row?.appointmentDescription);
      console.log('getAppointmentDescription - all keys:', row ? Object.keys(row) : 'row is null');
      
      // Try exact case first, then variations
      const value = row?.AppointmentDescription || row?.appointmentDescription || row?.ProcDescript || '';
      console.log('getAppointmentDescription - returning:', value);
      return value;
    },

    /**
     * Get AptStatus value (handle case variations)
     */
    getAptStatus(row) {
      return row.AptStatus || row.aptStatus || null;
    },

    codeWithDesc({ ProcCode, Descript }) {
      return `${ProcCode} — [${Descript}]`;
    },

    removeCode(option, filterType) {
      const filterArray = this.filters[filterType];
      if (filterArray && Array.isArray(filterArray)) {
        const removeIdx = filterArray.findIndex(each => {
          return each.ProcCode === option.ProcCode;
        });
        if (removeIdx > -1) {
          filterArray.splice(removeIdx, 1);
        }
      }
    },

    searchCodes(s) {
      s = s.trim();
      if (s !== '') {
        if (s.indexOf('*') > -1) {
          let str = s.substring(0, s.length - 1);
          for (let option of this.procCodes) {
            if (option.ProcCode && option.ProcCode.toUpperCase().startsWith(str.toUpperCase())) {
              // Optionally auto-add matching codes
              // This is just for search functionality, actual selection is handled by multiselect
            }
          }
        }
      }
    },


    async startComparison() {
      if (!this.canCompare) return;

      this.comparisonLoading = true;

      try {
        let fromDate, toDate;

        if (this.comparison.usePreviousYear) {
          // Calculate same period last year
          fromDate = moment(this.filters.fromDate).subtract(1, 'year');
          toDate = moment(this.filters.toDate).subtract(1, 'year');
          
          // Update the comparison date fields to show what we're comparing
          this.comparison.fromDate = fromDate.toDate();
          this.comparison.toDate = toDate.toDate();
        } else {
          fromDate = moment(this.comparison.fromDate);
          toDate = moment(this.comparison.toDate);
        }

        const payload = {
          siteID: this.siteID,
          fromDate: fromDate.format('YYYY-MM-DD'),
          toDate: toDate.format('YYYY-MM-DD')
        };

        const response = await this.getNewPatientReportComparisonData(payload);
        
        if (response) {
          this.comparisonReferralData = response.referralSources || [];
          this.showComparisonChart = true;
        }
      } catch (err) {
        console.error('Error getting comparison data:', err);
        this.$toast.error('Error loading comparison data');
      } finally {
        this.comparisonLoading = false;
      }
    },

    clearComparison() {
      this.showComparisonChart = false;
      this.comparisonReferralData = [];
      this.comparison.fromDate = null;
      this.comparison.toDate = null;
      this.comparison.usePreviousYear = false;
    },

    anchorMoment() {
      // Use the Huddle-selected date as anchor when present; otherwise fall back to "now"
      return this.huddleDate ? moment(this.huddleDate, 'YYYY-MM-DD') : moment();
    },

    setThisMonth() {
      const anchor = this.anchorMoment();
      this.filters.fromDate = anchor.clone().startOf('month').toDate();
      this.filters.toDate = anchor.clone().endOf('month').toDate();
    },

    setLastMonth() {
      this.filters.fromDate = moment().subtract(1, 'month').startOf('month').toDate();
      this.filters.toDate = moment().subtract(1, 'month').endOf('month').toDate();
    },

    resetFilters() {
      const anchor = this.anchorMoment();
      this.filters = {
        fromDate: anchor.clone().startOf('month').toDate(),
        toDate: anchor.clone().endOf('month').toDate(),
        provider: null,
        initialVisitCodes: [],
        referralSource: null,
        initialAptStatus: 'all',
        nextAptStatus: 'all',
        nextAptProcCodes: []
      };
      this.selectedInitialAptStatus = { value: 'all', label: 'Show All' };
      this.selectedNextAptStatus = { value: 'all', label: 'Show All' };
      this.search();
    },

    onInitialAptStatusChange(selected) {
      if (selected) {
        this.filters.initialAptStatus = selected.value;
      }
    },

    onNextAptStatusChange(selected) {
      if (selected) {
        this.filters.nextAptStatus = selected.value;
      }
    },

    onPagination(page) {
      this.currentPage = page;
    },

    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      if (this.$refs.patientTable) {
        this.$refs.patientTable.setPage(page);
      }
    },

    onDateChange() {
      // Could trigger auto-search on date change if desired
    },

    onPreviousYearChange(value) {
      if (value) {
        // Clear manual date selection when using previous year
        this.comparison.fromDate = null;
        this.comparison.toDate = null;
      }
    },

    closeSlideOutPanel() {
      this.clearNewPatientReportData();
      this.$emit('closePanel', {});
    },

    gotoPatientPage(patNum) {
      const teamioPatientID = `${this.siteID}-${patNum}`;
      const routeData = this.$router.resolve({ name: 'patientprofile', params: { patientID: teamioPatientID }});
      window.open(routeData.href, '_blank');
    },

    // Formatting helpers
    formatDateRange(fromDate, toDate) {
      if (!fromDate || !toDate) return '';
      return `${moment(fromDate).format('MM/DD/YYYY')} - ${moment(toDate).format('MM/DD/YYYY')}`;
    },

    getAptStatusVariant(status) {
      // Handle both string and number comparisons
      // Returns Bootstrap 4 badge class name (without 'badge-' prefix for use in class binding)
      const statusNum = parseInt(status, 10);
      if (statusNum === 1) return 'info';
      if (statusNum === 2) return 'success';
      return 'secondary';
    },

    getAptStatusText(status) {
      // Handle both string and number comparisons
      // Rule: "Scheduled" when AptStatus = 1, "Completed" when AptStatus = 2
      if (status === null || status === undefined || status === '') return '';
      
      // Convert to number for comparison (handles both string "2" and number 2)
      const statusNum = parseInt(status, 10);
      
      // Check if conversion was successful (not NaN)
      if (isNaN(statusNum)) return '';
      
      if (statusNum === 1) return 'Scheduled';
      if (statusNum === 2) return 'Completed';
      
      // If not 1 or 2, return empty string (don't show badge)
      return '';
    }
  }
};
</script>

<style src="./HuddleComponentsModern.scss" lang="scss" />

<style lang="scss" scoped>
// Match Huddle page typography
.new-patient-report {
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
  color: #334155;
}

// Top pagination bar
.top-pagination {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px 8px 0 0;
  border-bottom: none;

  .pagination-info {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;
  }

  .pagination-page {
    font-size: 0.8125rem;
    color: #334155;
    font-weight: 500;
    margin: 0 4px;
  }

  .gap-2 {
    gap: 0.375rem;
  }

  .btn-outline-secondary {
    padding: 4px 8px;
    border-color: #e2e8f0;
    color: #334155;

    &:hover:not(:disabled) {
      background-color: #f1f5f9;
      border-color: #e2e8f0;
    }

    &:disabled {
      background-color: #f8fafc;
      color: #94a3b8;
    }
  }
}

// Huddle-style table headers and cells
::v-deep .VueTables {
  .VueTables__search {
    input {
      border-radius: 8px;
      border: 1px solid #e2e8f0;
      padding: 10px 14px;
      font-size: 0.875rem;

      &:focus {
        border-color: #3abf94;
        box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.1);
      }
    }
  }

  table {
    thead th {
      font-size: 0.8125rem;
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      padding: 12px 16px;
      background-color: #f8fafc;
      border-bottom: 2px solid #e2e8f0;
    }

    tbody td {
      font-size: 0.9375rem;
      color: #334155;
      padding: 14px 16px;
      vertical-align: middle;
      border-bottom: 1px solid #e2e8f0;
    }

    tbody tr:hover {
      background-color: #f8fafc;
    }
  }

  .VuePagination {
    margin-top: 16px;

    .pagination {
      gap: 4px;

      .page-item {
        .page-link {
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          color: #334155;
          padding: 6px 12px;
          font-size: 0.8125rem;

          &:hover {
            background-color: #f1f5f9;
          }
        }

        &.active .page-link {
          background-color: #3abf94;
          border-color: #3abf94;
          color: white;
        }

        &.disabled .page-link {
          background-color: #f8fafc;
          color: #94a3b8;
        }
      }
    }
  }
}

// Widget header typography matching Huddle
::v-deep .widget {
  h5 {
    font-size: 1rem;
    font-weight: 600;
    color: #0f172a;
  }
}

// Patient link styling
::v-deep table a {
  color: #3abf94;
  font-weight: 500;
  text-decoration: none;

  &:hover {
    color: #2da77f;
    text-decoration: underline;
  }
}

// Horizontal bar chart for referral sources
.referral-bar-chart {
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;
}

.referral-bar-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #f1f5f9;
  }
}

.referral-bar-label {
  width: 130px;
  min-width: 130px;
  font-size: 0.8rem;
  color: #475569;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 575px) {
    width: 90px;
    min-width: 90px;
    font-size: 0.75rem;
  }
}

.referral-bar-track {
  flex: 1;
  height: 20px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
}

.referral-bar-fill {
  height: 100%;
  border-radius: 4px;
  min-width: 2px;
  transition: width 0.3s ease;
}

.referral-bar-count {
  width: 36px;
  min-width: 36px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #334155;
  text-align: right;
}

.table-responsive {
  overflow-y: auto;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch; // Smooth scrolling on iOS

  // Make table font smaller on mobile for better fit
  @media (max-width: 767px) {
    ::v-deep table {
      font-size: 0.85rem;

      th, td {
        padding: 0.5rem 0.4rem;
      }

      .badge {
        font-size: 0.75rem;
        padding: 0.25em 0.4em;
      }

      small {
        font-size: 0.75rem;
      }
    }
  }

  @media (max-width: 575px) {
    ::v-deep table {
      font-size: 0.8rem;

      th, td {
        padding: 0.4rem 0.3rem;
      }
    }
  }
}

// Ensure top panels have matching heights on larger screens
.top-section-row {
  display: flex;
  align-items: stretch;

  @media (min-width: 992px) {
    > .col-lg-6 {
      display: flex;
      flex-direction: column;

      > .widget {
        height: 100%;
        display: flex;
        flex-direction: column;

        ::v-deep .widget-body {
          flex: 1;
        }
      }
    }
  }

  // Stack on mobile without height matching
  @media (max-width: 991px) {
    display: block;
  }
}

// Quick date buttons responsive styling
.quick-date-buttons {
  .btn-primary {
    background: linear-gradient(135deg, #3abf94 0%, #2dd4bf 100%);
    border-color: #3abf94;
    color: white;

    &:hover, &:focus {
      background: linear-gradient(135deg, #2da77f 0%, #26b8a6 100%);
      border-color: #2da77f;
    }
  }

  .btn-outline-primary {
    color: #3abf94;
    border-color: #3abf94;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);

    &:hover, &:focus {
      background: linear-gradient(135deg, #3abf94 0%, #2dd4bf 100%);
      border-color: #3abf94;
      color: white;
    }
  }

  @media (max-width: 575px) {
    flex-direction: column;

    .btn {
      margin-bottom: 0.25rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

// Chart empty state styling
.chart-empty-state {
  min-height: 180px;
  padding: 24px 16px;
}

// Chart section wrapper - expanded background that fills the widget
.chart-section-wrapper {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 10px;
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

// Date range label - simpler text display (not a bar)
.date-range-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 8px 16px;

  i {
    color: #64748b;
    font-size: 0.9rem;
  }

  span {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #334155;
    letter-spacing: 0.2px;
  }
}

// Full height widget for chart section
::v-deep .chart-widget-full {
  .widget-body {
    height: 100%;
    display: flex;
    flex-direction: column;

    > div {
      flex: 1;
    }
  }
}


// Collapsed summary bar
.collapsed-summary-bar {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 16px;

  .summary-info {
    font-size: 0.875rem;
    color: #334155;
    font-weight: 500;
  }

  .gap-2 {
    gap: 0.5rem;
  }
}

// Action buttons responsive layout
.action-buttons {
  gap: 0.5rem;

  @media (max-width: 575px) {
    .btn {
      min-width: 100px;
    }
  }
}

/* Form label styling to match PaymentSettings */
.form-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: #495057;
  margin-bottom: 0.4rem;
  display: block;

  @media (max-width: 767px) {
    font-size: 0.85rem;
    margin-bottom: 0.3rem;
  }
}

// Optimize b-row spacing on mobile
::v-deep .widget-body {
  @media (max-width: 767px) {
    padding: 1rem 0.75rem;
  }
}

// Mobile container optimization
@media (max-width: 575px) {
  ::v-deep .container-fluid {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}

/* Form controls styling */
::v-deep .form-control {
  height: auto;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
  border-radius: 4px;
  border: 1px solid #ced4da;
  
  &:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
}

/* Multiselect styling to match PaymentSettings */
::v-deep .multiselect {
  min-height: 38px;

  .multiselect__tags {
    min-height: 38px;
    padding: 6px 40px 0 8px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    background: #fff;
    font-size: 0.9rem;

    @media (max-width: 767px) {
      min-height: 36px;
      padding: 5px 40px 0 6px;
      font-size: 0.85rem;
    }
  }

  .multiselect__single {
    margin-bottom: 6px;
    font-size: 0.9rem;
    color: #495057;

    @media (max-width: 767px) {
      margin-bottom: 5px;
      font-size: 0.85rem;
    }
  }

  .multiselect__placeholder {
    color: #6c757d;
    margin-bottom: 6px;
    padding-top: 0;

    @media (max-width: 767px) {
      margin-bottom: 5px;
      font-size: 0.85rem;
    }
  }

  .multiselect__select {
    height: 36px;

    @media (max-width: 767px) {
      height: 34px;
    }
  }

  .multiselect__option {
    padding: 10px 12px;
    min-height: 36px;
    font-size: 0.9rem;

    @media (max-width: 767px) {
      padding: 8px 10px;
      min-height: 34px;
      font-size: 0.85rem;
    }

    &--highlight {
      background: #547fff;
      color: white;
    }

    &--selected {
      background: #e3f2fd;
      color: #495057;
      font-weight: 500;

      &.multiselect__option--highlight {
        background: #547fff;
        color: white;
      }
    }
  }

  .multiselect__content-wrapper {
    border: 1px solid #ced4da;
    border-radius: 0 0 4px 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  // Mobile optimizations
  @media (max-width: 767px) {
    min-height: 36px;
  }
}

// FIX: Override the widget overflow:hidden from HuddleComponentsModern.scss
// This allows the table-responsive div inside to scroll properly
::v-deep .widget {
  overflow: visible !important;

  .widgetBody {
    overflow: visible !important;
  }
}

// Ensure table container allows scrolling
.table-responsive {
  max-height: calc(100vh - 250px); // Taller table when panels are collapsed
  min-height: 400px;
  overflow-y: auto !important;
  overflow-x: auto !important;
}
</style>
